<template>
  <div class="relative">
    <DatePicker
      :model-value="modelValue"
      @update:model-value="handleModelValueUpdate"
      ref="datepicker"
      :locale="userLocale"
      :first-day-of-week="2"
      :disabled="disabled"
      :attributes="attributes"
      :popover="{ placement, positionFixed: true }"
      :min-date="minDate"
      :max-date="maxDate"
      trim-weeks
    >
      <template #default="{ togglePopover }">
        <div class="flex z-1" @click="disabled ? null : togglePopover()">
          <button
            v-if="inputStyle === 'label'"
            :class="{
              '!cursor-not-allowed opacity-60': disabled,
              'enabled:hover:text-gray-900 group': !disabled,
            }"
            class="flex items-center text-sm text-gray-500 font-medium"
            :disabled="disabled"
          >
            <span class="flex whitespace-nowrap items-center">
              <CalendarIcon
                class="h-5 w-5 text-gray-500"
                :class="{ 'group-hover:text-gray-900': !disabled }"
              />
              <span class="ml-2">
                {{
                  modelValue
                    ? format(new Date(modelValue), "LLL d")
                    : emptyLabel ?? t("components.inputs.datepicker.empty")
                }}
              </span>
            </span>
          </button>
          <button
            v-else
            class="relative flex w-full cursor-pointer rounded-sm border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
            :class="{
              'opacity-60 !cursor-not-allowed': disabled,
            }"
          >
            <CalendarIcon
              class="text-gray-500 w-5 h-5 absolute inset-y-0 left-3 top-1/2 -translate-y-1/2"
            />
            <span
              class="pl-7 flex items-center"
              :class="{ 'text-gray-500': !modelValue }"
            >
              {{ dateLabel }}
            </span>
            <div
              class="absolute inset-y-0 right-3 flex items-center"
              v-if="hint"
            >
              <Tooltip :content="hint" origin="right">
                <HelpCircle class="w-4 h-4 text-gray-400" />
              </Tooltip>
            </div>
          </button>
        </div>
      </template>
      <template #footer>
        <div
          class="flex flex-row items-center justify-end p-4 border-t border-gray-200"
        >
          <span
            @click="clear"
            class="text-sm font-medium text-gray-600 hover:text-gray-600/80 cursor-pointer"
            >Clear date</span
          >
        </div>
      </template>
    </DatePicker>
  </div>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import { DatePicker } from "v-calendar";
import { useI18n } from "vue-i18n";
import { format, isEqual } from "date-fns";
import { computed, ref } from "vue";
import { CalendarIcon } from "@heroicons/vue/24/outline";
import HelpCircle from "@/Tailwind/Icon/HelpCircle.vue";
import Tooltip from "@/Tailwind/Components/Tooltip.vue";

const props = defineProps({
  modelValue: {
    type: [Boolean, String, Date],
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  inputStyle: {
    type: String,
    default: "input",
  },
  placement: String,
  minDate: {
    type: Date,
    default: null,
  },
  maxDate: {
    type: Date,
    default: null,
  },
  emptyLabel: String,
  hint: String,
});

const emit = defineEmits(["update:modelValue"]);
const { t } = useI18n();

const datepicker = ref();

const userLocale = usePage().props.locale ?? "en";

const attributes = [
  {
    key: "today",
    highlight: {
      fillMode: "solid",
      contentClass: "bg-brand-800 text-white current-day-highlight",
    },
    dates: new Date(),
  },
];

const dateLabel = computed(() => {
  if (props.modelValue && props.modelValue !== "") {
    return format(new Date(props.modelValue), "LLL d, yyyy");
  }

  return t("components.inputs.datepicker.select_date");
});

/**
 * The <DatePicker> component uses :model-value="modelValue", which triggers
 * an @update:model-value event whenever the modelValue prop of this component changes.
 * This causes an event to be emitted, even though nothing has actually changed.
 * Only emit if the new date is different from the previous one.
 *
 * @param {Date} value
 */
const handleModelValueUpdate = (value) => {
  if (!value && !props.modelValue) {
    return;
  }

  if (isEqual(new Date(props.modelValue), value)) {
    return;
  }

  return emit("update:modelValue", value);
};

const clear = () => {
  emit("update:modelValue", null);
  datepicker.value.hidePopover();
};
</script>
